import DashboardAnalyticsRangeSelector from '@/components/DashboardAnalyticsRangeSelector';
import { VersionsHistoryPopover } from '@/components/DynamicPages/VersionsHistoryPopover.tsx';
import { DynamicPageSettingsModal } from '@/components/ManageDynamicPage/DynamicPageSettingsModal.tsx';
import { DynamicPageSidebar } from '@/components/ManageDynamicPage/DynamicPageSidebar';
import AnalyticsFilterBuilder from '@/components/Overview/AnalyticsFilterBuilder';
import { PresenceIndicator } from '@/components/Presence/PresenceIndicator';
import { AuthContext } from '@/contexts/AuthContext';
import { usePresence } from '@/hooks/usePresence';
import { classNames } from '@/utils';
import { DynamicPage } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Loader from '@superfunnel/ui-core/Loader';
import MenuTabs, { MenuTab } from '@superfunnel/ui-core/MenuTabs';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { Popover, PopoverAnchor } from '@superfunnel/ui-core/v2/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import {
  ClockRotateRight,
  MoreVert,
  NavArrowRight,
  SparksSolid,
} from 'iconoir-react';
import Lottie from 'lottie-react';
import { EyeOff, Settings } from 'lucide-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDashboardContext } from '../../contexts/DashboardContext';
import { useDynamicPageContext } from '../../contexts/DynamicPageContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import CopyDynamicPageLinkModal from '../../modals/CopyDynamicPageLinkModal';
import EditVariantConstraintsModal from '../../modals/EditVariantConstraintsModal';
import PublishDynamicPageVersionDrawer from '../../modals/PublishDynamicPageVersionDrawer';
import SubscriptionRequiredModal from '../../modals/SubscriptionRequiredModal';
import { unpublishDynamicPage } from '../../services/DynamicPagesService';

export default function ManageDynamicPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const { isSupportAdmin } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { show } = useContext(SnackbarContext);
  const { hasActiveSubscription } = useDashboardContext();
  const {
    selectedDynamicPage,
    variants,
    websites,
    hasChangesToDeploy,
    relevantDomains,
    primaryDomain,
    analyticsFilters,
    setAnalyticsFilters,
    resetAnalyticsFilters,
  } = useDynamicPageContext();

  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  const [websiteBuildingAnimation, setWebsiteBuildingAnimation] =
    useState<any>(null);
  const [copyLinkModalOpen, setCopyLinkModalOpen] = useState(false);
  const [openSettings, setOpenSettings] = useState<{
    open: boolean;
    dynamicPageId: string;
    dynamicPageData: DynamicPage;
  }>();
  const [agentSidebarCollapsed, setAgentSidebarCollapsed] = useState(true);

  // Presence tracking for this dynamic page
  const { users: presentUsers } = usePresence({
    mode: 'page',
    accountId: selectedAccount?.id || '',
    dynamicPageId: selectedDynamicPage?.id || '',
  });
  const [editVariantConstraintsModelOpen, setEditVariantConstraintsModelOpen] =
    useState<{ variantId: string }>();
  const [
    publishDynamicPageVersionDrawerOpen,
    setPublishDynamicPageVersionDrawerOpen,
  ] = useState(false);
  const [openVersionsHistory, setOpenVersionsHistory] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<{
    title: string;
    description: string;
    actionTitle: string;
    onConfirm: () => void | Promise<void>;
  }>();
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState<{
      entity: string;
      name: string;
      onConfirm: () => void | Promise<void>;
    }>();

  useEffect(() => {
    import('../../assets/lotties/website-building.json').then((module) =>
      setWebsiteBuildingAnimation(module.default),
    );
  }, []);

  const currentTab = useMemo(() => {
    return location.pathname.split('/')[5];
  }, [location.pathname]);

  const tabs: MenuTab[] = useMemo(() => {
    return [
      { id: 'details', name: 'Details' },
      { id: 'adMap', name: 'Ad Map' },
      { id: 'analytics', name: 'Analytics' },
      { id: 'leads', name: 'Leads' },
      { id: 'experiments', name: 'Experiments' },
    ].map((t) => ({ ...t, current: t.id === currentTab }));
  }, [currentTab]);

  // Variant options for the filter builder
  const variantOptions = useMemo(() => {
    if (!variants) return [];
    return variants.map((v) => ({
      value: v.id,
      label: v.data().name,
    }));
  }, [variants]);

  // Experiment variation options - only includes websites from variants with experiments
  const experimentOptions = useMemo(() => {
    if (!variants || !websites) return [];

    // Find variants that have experiments (more than 1 website or have currentExperimentId)
    const variantsWithExperiments = variants.filter((v) => {
      const data = v.data();
      return data.websiteIds.length > 1 || data.currentExperimentId;
    });

    if (variantsWithExperiments.length === 0) return [];

    // Collect all websiteIds from variants with experiments
    const experimentWebsiteIds = new Set<string>();
    variantsWithExperiments.forEach((v) => {
      v.data().websiteIds.forEach((wsId) => experimentWebsiteIds.add(wsId));
    });

    // Map websiteIds to options with labels
    return websites
      .filter((ws) => experimentWebsiteIds.has(ws.id))
      .map((ws) => ({
        value: ws.id,
        label: ws.data().name || ws.id,
      }));
  }, [variants, websites]);

  if (!variants || !selectedDynamicPage) {
    return (
      <div className="flex w-full justify-center pt-24">
        <Loader />
      </div>
    );
  }

  if (
    selectedDynamicPage?.data().creationStatus === 'pending' &&
    !websites?.length
  ) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="mx-auto w-full max-w-md">
          {websiteBuildingAnimation && (
            <Lottie
              animationData={websiteBuildingAnimation}
              loop
              className="mx-auto max-w-md"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <DynamicPageSettingsModal
        open={openSettings?.open || false}
        setOpen={(open) =>
          setOpenSettings((prev) =>
            open ? (prev ? { ...prev, open } : undefined) : undefined,
          )
        }
        dynamicPage={openSettings?.dynamicPageData}
        dynamicPageId={openSettings?.dynamicPageId || ''}
      />
      <CopyDynamicPageLinkModal
        relevantDomains={relevantDomains}
        primaryDomain={primaryDomain}
        dynamicPage={selectedDynamicPage?.data()}
        open={copyLinkModalOpen}
        onClose={() => setCopyLinkModalOpen(false)}
      />
      <EditVariantConstraintsModal
        variantId={editVariantConstraintsModelOpen?.variantId}
        open={!!editVariantConstraintsModelOpen}
        onClose={() => setEditVariantConstraintsModelOpen(undefined)}
      />
      <PublishDynamicPageVersionDrawer
        open={publishDynamicPageVersionDrawerOpen}
        setOpen={setPublishDynamicPageVersionDrawerOpen}
      />
      <AlertModal
        type={'warn'}
        open={!!confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(undefined)}
        title={confirmationModalOpen?.title || ''}
        description={confirmationModalOpen?.description || ''}
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => setConfirmationModalOpen(undefined),
          },
          {
            title: confirmationModalOpen?.actionTitle || 'Confirm',
            type: 'primary',
            loading: unpublishing,
            onClick: async () => {
              await confirmationModalOpen?.onConfirm();
              setConfirmationModalOpen(undefined);
            },
          },
        ]}
      />
      <AlertModal
        type={'warn'}
        open={!!deleteConfirmationModalOpen}
        onClose={() => setDeleteConfirmationModalOpen(undefined)}
        title={`Are you sure you wish to delete ${deleteConfirmationModalOpen?.entity} "${deleteConfirmationModalOpen?.name}"?`}
        description="This action cannot be undone."
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            onClick: () => setDeleteConfirmationModalOpen(undefined),
          },
          {
            title: 'Delete',
            type: 'primary',
            // color: 'negative',
            onClick: async () => {
              await deleteConfirmationModalOpen?.onConfirm();
              setDeleteConfirmationModalOpen(undefined);
            },
          },
        ]}
      />
      <div className="h-full w-full">
        <div
          className={classNames(
            'flex min-h-screen w-full flex-1 flex-col px-3 py-3 transition-all duration-300 ease-in-out lg:space-y-6 lg:py-6 lg:ps-10',
            agentSidebarCollapsed ? 'lg:pe-16' : 'lg:pe-104',
          )}
        >
          {/* <div className="absolute top-0 z-10 hidden h-8 w-full bg-white lg:block"></div> */}
          <div className="top-[22px] z-10 flex flex-col bg-white">
            <div className="font-inter flex flex-col items-stretch justify-between gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:gap-0 sm:gap-y-4">
              <div className="gap-0 sm:flex-auto">
                <div className="flex items-center gap-1">
                  <h1 className="text-base-500 text-xs">dynamic pages</h1>
                  <NavArrowRight className="text-brand-900 h-3 w-3" />
                  <h1 className="text-base-900 text-xs font-medium">
                    {selectedDynamicPage.data().name}
                  </h1>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p
                      className="text-brand-600 hover:text-brand-800 mt-2 w-min max-w-full cursor-pointer text-sm whitespace-nowrap underline"
                      onClick={() => {
                        if (!selectedDynamicPage.data().isLive) {
                          show(
                            'This page has not been published yet. Please publish the page first.',
                            'warn',
                          );
                          return;
                        }
                        setCopyLinkModalOpen(true);
                      }}
                    >
                      {`https://${primaryDomain?.data().domain}/${selectedDynamicPage.data().path}`}
                    </p>
                  </TooltipTrigger>
                  {!selectedDynamicPage.data().isLive && (
                    <TooltipContent side="bottom" align="start">
                      Page not published. The link will not work until you
                      publish the page.
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>

              <div className="flex items-center gap-3">
                {/* {hasChangesToDeploy && (
                <div className="pe-2">
                  <Badge title="Changes to Publish" hideDot color="yellow" />
                </div>
              )} */}
                <div className="flex-1 sm:hidden" />
                {presentUsers.length > 0 && (
                  <PresenceIndicator users={presentUsers} maxVisible={5} />
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" onClick={() => {}} className="h-10">
                      <SparksSolid className="text-brand-500 h-5 w-5" />
                      AI Suggestions
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="center">
                    Coming soon.
                  </TooltipContent>
                </Tooltip>
                <Button
                  variant="default"
                  onClick={() => {
                    if (!hasActiveSubscription && !isSupportAdmin) {
                      setSubscriptionModalOpen(true);
                      return;
                    }
                    setPublishDynamicPageVersionDrawerOpen(true);
                  }}
                  className={classNames(
                    'h-10',
                    hasChangesToDeploy && 'gap-3 pe-2',
                  )}
                >
                  Publish
                  {hasChangesToDeploy && (
                    <div className="pointer-events-none rounded-lg bg-[#c5adff80] px-2 py-1 text-xs font-medium text-white">
                      changes to publish
                    </div>
                  )}
                </Button>

                <Popover
                  modal={false}
                  open={openVersionsHistory}
                  onOpenChange={(open) =>
                    !open && setOpenVersionsHistory(false)
                  }
                >
                  <PopoverAnchor>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="h-10 w-10">
                          <MoreVert className="h-5 w-5 text-gray-600" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            setOpenSettings({
                              open: true,
                              dynamicPageId: selectedDynamicPage.id,
                              dynamicPageData: selectedDynamicPage.data(),
                            })
                          }
                        >
                          <Settings className="h-4 w-4" /> Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={unpublishing}
                          onClick={() => {
                            setConfirmationModalOpen({
                              title: `Are you sure you want to unpublish "${selectedDynamicPage?.data().name || 'this page'}"?`,
                              description:
                                'This will remove the page from your live site. Visitors will no longer be able to access it until you publish again.',
                              actionTitle: 'Unpublish',
                              onConfirm: async () => {
                                if (!selectedAccount || !selectedDynamicPage) {
                                  return;
                                }
                                setUnpublishing(true);
                                try {
                                  await unpublishDynamicPage(
                                    selectedAccount.id,
                                    selectedDynamicPage.id,
                                  );
                                  show(
                                    'Page unpublished successfully',
                                    'success',
                                  );
                                } catch (error) {
                                  console.error(
                                    'Failed to unpublish page:',
                                    error,
                                  );
                                  show('Failed to unpublish page', 'warn');
                                } finally {
                                  setUnpublishing(false);
                                }
                              },
                            });
                          }}
                        >
                          <EyeOff className="h-4 w-4" />
                          {unpublishing ? 'Unpublishing...' : 'Unpublish page'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setTimeout(() => {
                              setOpenVersionsHistory(true);
                            }, 300);
                          }}
                        >
                          <ClockRotateRight className="h-4 w-4" />
                          Versions History
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </PopoverAnchor>
                  <VersionsHistoryPopover
                    open={openVersionsHistory}
                    close={() => setOpenVersionsHistory(false)}
                  />
                </Popover>
              </div>
            </div>
            <div className="border-base-200 mt-4 flex items-center justify-between gap-4 border-b">
              <MenuTabs
                className="font-inter border-0"
                tabs={tabs}
                onTabClick={(tab) => {
                  navigate(`./${tab}`);
                }}
              />

              <div className="flex shrink-0 items-center gap-2 pb-1.5">
                {currentTab === 'analytics' && (
                  <AnalyticsFilterBuilder
                    size="sm"
                    mode="dynamicPage"
                    variantOptions={variantOptions}
                    experimentOptions={experimentOptions}
                    filters={analyticsFilters}
                    onFiltersChange={setAnalyticsFilters}
                    onFiltersReset={resetAnalyticsFilters}
                  />
                )}
                <DashboardAnalyticsRangeSelector size="small" variant="text" />
              </div>
            </div>
          </div>

          <div className="relative flex-1 overflow-scroll">
            <Outlet />
          </div>
        </div>

        <div
          className={`fixed top-0 right-0 bottom-0 z-10 hidden transition-all duration-300 ease-in-out sm:block ${
            agentSidebarCollapsed
              ? 'w-8 translate-x-0 overflow-hidden opacity-100 hover:w-10'
              : 'w-96 translate-x-0 opacity-100'
          }`}
        >
          <DynamicPageSidebar
            collapsed={agentSidebarCollapsed}
            onCollapseChange={setAgentSidebarCollapsed}
          />
        </div>
      </div>
      <SubscriptionRequiredModal
        open={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
      />
    </>
  );
}
